import { useCallback, useEffect, useState } from 'react';
import { useGetApiData } from '@grc/ui-utility';
import getOrderDetails from '../../../../service/api/get-order-details';
import { GetTransportById } from '../../../../service/api/get-transport-by-id';
import { getTrackedTransportationDetailsById } from '../../../../service/api/get-tracked-transportation-details-by-id';
import {
  fetchOrderById,
  fetchTrackedTransportsByIdUrl
} from '../../../../common/constants/url-constants';
import { trackingService } from '../../../../common/constants/constants';
import { IShipmentDetail } from '../../../../models/shipments/shipment-details.interface';
import { mergeStopsAndUpdates } from './utils/merge-stops-and-status-updates';
import { filterTransportStops } from './utils/filter-transport-stops';
import { resolveShipmentTransportIds } from '../../utils/resolve-shipment-transport-ids';

// Derives which transports carry this shipment (to enable/disable the
// header's Tracking Updates button exactly like the reference app), and -
// on demand - fetches + merges the stop/status timeline for the drawer.
// Everything here is page-local state; no new global store (see plan's
// "no new store" note, matching the rest of the Shipments feature).
export function useTrackingUpdates(shipment: IShipmentDetail | undefined) {
  const { fetchData } = useGetApiData();
  const [transportIds, setTransportIds] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [stops, setStops] = useState<any[]>([]);
  const [statusUpdates, setStatusUpdates] = useState<any[]>([]);

  useEffect(() => {
    let isCancelled = false;

    const load = async () => {
      if (!shipment?.orderId) {
        setTransportIds([]);
        return;
      }

      const order = await getOrderDetails(
        shipment.orderId,
        fetchOrderById,
        fetchData
      );
      if (isCancelled) return;

      setTransportIds(
        resolveShipmentTransportIds(order, shipment.shipmentNumber)
      );
    };

    load();

    return () => {
      isCancelled = true;
    };
  }, [fetchData, shipment?.orderId, shipment?.shipmentNumber]);

  const isTrackingEnabled =
    shipment?.status !== undefined &&
    shipment.status !== 0 &&
    shipment.status !== 1 &&
    transportIds.length > 0;

  const openDrawer = useCallback(async () => {
    if (!isTrackingEnabled || !shipment) return;

    const transportId = transportIds[0];
    const transport = await GetTransportById(transportId, fetchData);
    const trackedTransport: any = await getTrackedTransportationDetailsById(
      fetchTrackedTransportsByIdUrl.concat(`/${transportId}`),
      fetchData,
      trackingService
    );

    const filteredStops = filterTransportStops(
      trackedTransport?.stops,
      shipment.shipmentNumber,
      trackedTransport?.transportationNumber,
      transportId,
      transport?.carrier?.carrierId
    );
    const stopsById = Object.fromEntries(
      filteredStops.map((stop: any) => [stop.stopSequence, stop])
    );

    const merged = mergeStopsAndUpdates(
      stopsById,
      shipment.shipmentNumber,
      trackedTransport?.transportationNumber,
      transportId,
      transport?.carrier?.carrierId,
      trackedTransport?.locationUpdates ?? []
    );

    setStops(filteredStops);
    setStatusUpdates(merged);
    setIsOpen(true);
  }, [fetchData, isTrackingEnabled, shipment, transportIds]);

  const closeDrawer = useCallback(() => setIsOpen(false), []);

  return {
    isOpen,
    isTrackingEnabled: !!isTrackingEnabled,
    openDrawer,
    closeDrawer,
    stops,
    statusUpdates
  };
}

export default useTrackingUpdates;
